import React, { useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Role } from "types";
import { getUserDetails } from "components/service/userService/userService";
import { mixpanelCustomEvent } from "mixpanel/eventTriggers";
import { MixpanelEventName } from "mixpanel/types";

const UserProvisioning: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, user } = useUser();

  const mixpanelProps = {
    $name: `${user?.fullName}`,
    $email: user?.emailAddresses[0]?.emailAddress,
  };

  useEffect(() => {
    const updateUserRoles = async () => {
      if (!isSignedIn || !user) {
        return;
      }

      let currentRoles: string[] = [];
      try {
        const response = await getUserDetails();
        currentRoles = response.roles || [];
      } catch (error) {
        console.error("Failed to fetch user details from API:", error);
      }

      const searchString = location.hash.replace(/^#\/?/, "");
      const outerUrl = new URL(searchString, window.location.origin);
      const finalRedirectUrl = outerUrl.searchParams.get("redirect_url");

      let appParam = null;
      if (finalRedirectUrl) {
        try {
          const finalUrl = new URL(finalRedirectUrl);
          appParam = finalUrl.searchParams.get("app");
        } catch (err) {
          console.error(
            "Failed to parse final redirect URL for app param:",
            err
          );
        }
      }

      const validRoles = Object.values(Role);
      const newRole = appParam as Role;
      const shouldUpdateRole =
        validRoles.includes(newRole) && !currentRoles.includes(newRole);

      const handleRedirect = () => {
        mixpanelCustomEvent({
          mixpanelProps: {
            ...mixpanelProps,
            redirectedTo: finalRedirectUrl,
          },
          id: user?.id?.toString() || "",
          eventName: finalRedirectUrl
            ? MixpanelEventName.satelliteAppUserLoggedInThroughAccountsAppAndNavigatedBackToSateliteApp
            : MixpanelEventName.noRedirectUrlFound,
        });
        if (finalRedirectUrl) {
          setTimeout(() => {
            window.location.href = finalRedirectUrl;
          }, 500);
        } else {
          navigate("/");
        }
      };

      if (shouldUpdateRole) {
        const updatedRoles = [...new Set([...currentRoles, newRole])];

        user
          .update({
            unsafeMetadata: { roles: updatedRoles },
          })
          .then(() => {
            mixpanelCustomEvent({
              mixpanelProps: {
                ...mixpanelProps,
              },
              id: user?.id?.toString() || "",
              eventName: MixpanelEventName.rolesUpdatedForUser,
            });
            handleRedirect();
          })
          .catch((error) => {
            console.error("Error updating user metadata:", error);
            handleRedirect();
          });
      } else {
        mixpanelCustomEvent({
          mixpanelProps: {
            ...mixpanelProps,
          },
          id: user?.id?.toString() || "",
          eventName: MixpanelEventName.noNeededToUpdateRolesForUser,
        });
        handleRedirect();
      }
    };

    updateUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user, location.hash, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-lg">
      <p className="animate-pulse text-lg font-semibold">
        Updating your account details...
      </p>
      <p className="text-sm font-normal">
        Please wait while we prepare your session.
      </p>
    </div>
  );
};

export default UserProvisioning;
